import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { getSubAgents, getInsights, generateSubAgentInsights } from '../services/agentApi';
import {
    ArrowLeft,
    Plus,
    Edit2,
    Loader,
    Database,
    Bell,
    TrendingUp,
    Shield,
    Package,
    Users,
    Activity,
    MessageCircle,
    Lightbulb,
    CreditCard,
    Megaphone,
    Utensils,
    Truck,
    Layers,
    ShoppingCart
} from 'lucide-react';

/**
 * SubAgentsView - Page showing all sub-agents for a database in a grid layout.
 * Design based on mark.md image reference.
 */
export const SubAgentsView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { databases } = useDatabase();
    const { showToast } = useToast();

    const databaseFromState = location.state?.database;
    const [subAgents, setSubAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentDatabase, setCurrentDatabase] = useState(null);
    const [insightsCountMap, setInsightsCountMap] = useState({});
    const [loadingInsights, setLoadingInsights] = useState(false);
    const [generatingAgentId, setGeneratingAgentId] = useState(null);

    // Get agent icon based on name/type
    const getAgentIcon = (agentName) => {
        const name = agentName.toLowerCase();
        
        // 1. Payment & Conversion
        if (name.includes('payment') || name.includes('conversion') || name.includes('transaction') || name.includes('checkout')) {
            return { icon: CreditCard, bg: 'bg-primary-100', color: 'text-primary-600' };
        }
        
        // 2. Marketing
        if (name.includes('marketing') || name.includes('campaign') || name.includes('promotion') || name.includes('coupon') || name.includes('offer')) {
            return { icon: Megaphone, bg: 'bg-purple-100', color: 'text-purple-600' };
        }
        
        // 3. Restaurant
        if (name.includes('restaurant') || name.includes('food') || name.includes('dining') || name.includes('menu')) {
            return { icon: Utensils, bg: 'bg-orange-100', color: 'text-orange-600' };
        }
        
        // 4. Delivery
        if (name.includes('delivery') || name.includes('logistics') || name.includes('route') || name.includes('shipping') || name.includes('driver')) {
            return { icon: Truck, bg: 'bg-blue-100', color: 'text-blue-600' };
        }
        
        // 5. Inventory & Stock (KEEP AS IS)
        if (name.includes('inventory') || name.includes('stock') || name.includes('warehouse')) {
            return { icon: Package, bg: 'bg-orange-100', color: 'text-orange-600' };
        }
        
        // 6. Product Catalog
        if (name.includes('product') || name.includes('catalog') || name.includes('sku') || name.includes('category')) {
            return { icon: Layers, bg: 'bg-orange-100', color: 'text-orange-600' };
        }
        
        // 7. Customer Insights (KEEP AS IS)
        if (name.includes('customer') || name.includes('behavior') || name.includes('user') || name.includes('demographic') || name.includes('loyalty')) {
            return { icon: Users, bg: 'bg-primary-100', color: 'text-primary-600' };
        }
        
        // 8. Order Performance
        if (name.includes('order') || name.includes('sales')) {
            return { icon: ShoppingCart, bg: 'bg-orange-100', color: 'text-orange-600' };
        }

        // Defaults and others
        if (name.includes('trend') || name.includes('financial') || name.includes('cost')) return { icon: TrendingUp, bg: 'bg-primary-100', color: 'text-primary-600' };
        if (name.includes('optimizer')) return { icon: Package, bg: 'bg-orange-100', color: 'text-orange-600' };
        if (name.includes('vendor') || name.includes('supplier')) return { icon: Users, bg: 'bg-primary-100', color: 'text-primary-600' };
        if (name.includes('anomaly') || name.includes('security') || name.includes('fraud') || name.includes('alert') || name.includes('detection')) return { icon: Shield, bg: 'bg-red-100', color: 'text-red-600' };
        if (name.includes('activity') || name.includes('engagement') || name.includes('api') || name.includes('usage')) return { icon: Activity, bg: 'bg-cyan-100', color: 'text-cyan-600' };
        
        return { icon: Bell, bg: 'bg-gray-100', color: 'text-gray-600' };
    };


    // Fetch sub-agents
    const fetchSubAgents = useCallback(async () => {
        if (!currentDatabase?.id) return;

        setLoading(true);
        try {
            const response = await getSubAgents(currentDatabase.id);
            if (response.data?.data?.sub_agents) {
                setSubAgents(response.data.data.sub_agents);
            }
        } catch (error) {
            console.error('Failed to fetch sub-agents:', error);
            showToast('Failed to load sub-agents', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentDatabase?.id, showToast]);

    // Fetch insights count for each sub-agent
    const fetchInsightsCounts = useCallback(async (agents) => {
        if (!currentDatabase?.id || agents.length === 0) return;

        setLoadingInsights(true);
        try {
            const countsMap = {};
            for (const agent of agents) {
                try {
                    const insightsRes = await getInsights(currentDatabase.id, 'sub_agent', agent.id);
                    if (insightsRes.data?.data?.insights) {
                        countsMap[agent.id] = insightsRes.data.data.insights.length;
                    }
                } catch (error) {
                    console.debug(`Could not fetch insights for agent ${agent.id}:`, error);
                    countsMap[agent.id] = 0;
                }
            }
            setInsightsCountMap(countsMap);
        } catch (error) {
            console.error('Failed to fetch insights counts:', error);
        } finally {
            setLoadingInsights(false);
        }
    }, [currentDatabase?.id]);

    // Generate insights for a sub-agent
    const handleGenerateInsights = useCallback(async (agentId, agentName) => {
        setGeneratingAgentId(agentId);
        try {
            const response = await generateSubAgentInsights(agentId);
            if (response.data?.status === 'success') {
                const data = response.data.data || {};
                const warning = data.warning;
                const createdIds = Array.isArray(data.insight_plan_ids)
                    ? data.insight_plan_ids
                    : [];
                const totalCount =
                    typeof data.count === 'number' ? data.count : 0;
                const nNew = createdIds.length;

                setInsightsCountMap((prev) => ({
                    ...prev,
                    [agentId]: totalCount,
                }));

                // Early exit: no schema tables matched agent domain (no LLM call)
                if (warning && nNew === 0) {
                    showToast(warning, 'warning', 12000);
                    await fetchInsightsCounts(subAgents);
                    return;
                }

                if (warning) {
                    showToast(warning, 'warning', 12000);
                }

                if (nNew > 0) {
                    showToast(
                        `Generated ${nNew} new insight${nNew === 1 ? '' : 's'} for ${agentName}`,
                        'success'
                    );
                    navigate(`/agents/insights/${agentId}`, {
                        state: {
                            agentName,
                            databaseId: currentDatabase?.id,
                        },
                    });
                } else if (!warning) {
                    showToast(`No new insights returned for ${agentName}`, 'info', 6000);
                    navigate(`/agents/insights/${agentId}`, {
                        state: {
                            agentName,
                            databaseId: currentDatabase?.id,
                        },
                    });
                }
            }
        } catch (error) {
            console.error('Failed to generate insights:', error);
            showToast('Failed to generate insights', 'error');
        } finally {
            setGeneratingAgentId(null);
        }
    }, [
        showToast,
        navigate,
        currentDatabase?.id,
        fetchInsightsCounts,
        subAgents,
    ]);

    const { databaseId: databaseIdParam } = useParams();

    useEffect(() => {
        // Set database from state or find from context using route param
        const db = databaseFromState || databases.find(db => db.id === parseInt(databaseIdParam));
        if (db) {
            setCurrentDatabase(db);
        }
    }, [databaseFromState, databases, databaseIdParam]);

    useEffect(() => {
        if (currentDatabase) {
            fetchSubAgents();
        }
    }, [currentDatabase, fetchSubAgents]);

    // Fetch insights counts after sub-agents are loaded
    useEffect(() => {
        if (subAgents.length > 0) {
            fetchInsightsCounts(subAgents);
        }
    }, [subAgents, fetchInsightsCounts]);

    if (!currentDatabase) {
        return (
            <div className="page-content">
                <div className="flex justify-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={() => navigate('/agents')}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to AI Agents
                </button>

                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div>
                            <h1 className="page-title">{currentDatabase.name}</h1>
                            <p className="text-gray-600 mt-1">
                                Select a domain expert agent to analyze your database and get AI-powered insights
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={() => navigate('/agents/sub-agent/create', { state: { database: currentDatabase } })}
                            className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            <span>Add Sub-Agents</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Sub-Agents Grid */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            ) : subAgents.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                    <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No Sub-Agents Yet</h3>
                    <p className="text-gray-600 mb-6">
                        Create sub-agents to analyze different aspects of your database
                    </p>
                    <button
                        onClick={() => navigate('/agents/sub-agent/create', { state: { database: currentDatabase } })}
                        className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 inline-flex items-center space-x-2 transition-colors"
                    >
                        <Plus className="w-5 h-5" />
                        <span>Create Your First Sub-Agent</span>
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {subAgents.map((agent) => {
                        const agentIconConfig = getAgentIcon(agent.name);
                        const AgentIcon = agentIconConfig.icon;

                        return (
                            <div
                                key={agent.id}
                                onClick={() => navigate(`/agents/insights/${agent.id}`, {
                                    state: {
                                        agentName: agent.name,
                                        databaseId: currentDatabase.id
                                    }
                                })}
                                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all cursor-pointer flex flex-col"
                            >
                                {/* Header - Fixed height section */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-start space-x-3 min-w-0 flex-1">
                                        <div className={`w-10 h-10 ${agentIconConfig.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                                            <AgentIcon className={`w-5 h-5 ${agentIconConfig.color}`} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="font-semibold text-gray-900 truncate" title={agent.name}>{agent.name}</h3>
                                            <p className="text-sm text-gray-500 truncate" title={agent.focus || 'Domain Expert'}>{agent.focus || 'Domain Expert'}</p>
                                        </div>
                                    </div>
                                    {/* Insights Count Badge */}
                                    {loadingInsights ? (
                                        <Loader className="w-4 h-4 animate-spin text-gray-400 flex-shrink-0 ml-2" />
                                    ) : (insightsCountMap[agent.id] || 0) > 0 ? (
                                        <div className="flex items-center space-x-1 px-2 py-1 bg-primary-50 border border-primary-200 rounded-lg flex-shrink-0 ml-2">
                                            <Lightbulb className="w-3.5 h-3.5 text-primary-600" />
                                            <span className="text-xs font-medium text-primary-700">{insightsCountMap[agent.id]}</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center space-x-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded-lg flex-shrink-0 ml-2">
                                            <Lightbulb className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-xs font-medium text-gray-500">0</span>
                                        </div>
                                    )}
                                </div>

                                {/* Description - Fixed 3 lines with proper containment */}
                                <div className="mb-4">
                                    <p className="text-sm text-gray-600 line-clamp-3 overflow-hidden">
                                        {agent.description || `Analyze ${agent.focus || 'data'} patterns and provide actionable insights for business optimization.`}
                                    </p>
                                </div>

                                {/* Connected Data Sources - Constrained height */}
                                {agent.tables && agent.tables.length > 0 && (
                                    <div className="mb-4">
                                        <p className="text-xs font-medium text-gray-500 mb-2 truncate">Connected Data Sources</p>
                                        <div className="flex flex-wrap gap-1.5 max-h-16 overflow-hidden">
                                            {agent.tables.slice(0, 3).map((table, idx) => (
                                                <span
                                                    key={idx}
                                                    className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded flex-shrink-0"
                                                >
                                                    {table}
                                                </span>
                                            ))}
                                            {agent.tables.length > 3 && (
                                                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded flex-shrink-0">
                                                    +{agent.tables.length - 3}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Spacer to push actions to bottom */}
                                <div className="flex-grow"></div>

                                {/* Actions - Fixed at bottom */}
                                <div className="flex items-center space-x-2 pt-4 border-t border-gray-100 mt-auto" onClick={(e) => e.stopPropagation()}>
                                    {loadingInsights || generatingAgentId === agent.id ? (
                                        <button
                                            disabled
                                            className="flex-1 px-4 py-2.5 bg-gray-400 text-white rounded-lg font-medium text-sm cursor-not-allowed flex items-center justify-center space-x-2"
                                        >
                                            <Loader className="w-4 h-4 animate-spin flex-shrink-0" />
                                            <span className="truncate">Generating Insights...</span>
                                        </button>
                                    ) : (insightsCountMap[agent.id] || 0) > 0 ? (
                                        <button
                                            onClick={() => navigate(`/agents/insights/${agent.id}`, {
                                                state: {
                                                    agentName: agent.name,
                                                    databaseId: currentDatabase.id
                                                }
                                            })}
                                            className="flex-1 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm transition-colors flex items-center justify-center space-x-2 min-w-0"
                                        >
                                            <Lightbulb className="w-4 h-4 flex-shrink-0" />
                                            <span className="truncate">View Agent</span>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleGenerateInsights(agent.id, agent.name)}
                                            className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm transition-colors flex items-center justify-center space-x-2 min-w-0"
                                        >
                                            <Lightbulb className="w-4 h-4 flex-shrink-0" />
                                            <span className="truncate">Generate Insights</span>
                                        </button>
                                    )}
                                    <button
                                        onClick={() => navigate(`/agents/sub-agent/${agent.id}/edit`, { state: { database: currentDatabase } })}
                                        className="p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
                                        title="Edit Agent"
                                    >
                                        <Edit2 className="w-4 h-4 text-gray-600" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Floating Chat Button - Bottom Right */}
            <button
                onClick={() => navigate(`/agents/chat/${currentDatabase.id}`, {
                    state: {
                        database: currentDatabase
                    }
                })}
                className="fixed bottom-6 right-6 px-6 py-3 bg-primary-600 text-white rounded-full hover:bg-primary-700 shadow-lg transition-all flex items-center space-x-2 z-50 font-medium"
            >
                <MessageCircle className="w-5 h-5" />
                <span>Chat with Super Agent</span>
            </button>
        </div>
    );
};
